import {
  Button,
  ButtonGroup,
  closeCurrentDialog,
  DataStatus,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  showNotification,
} from 'platform/components';
import {Grid, Text, VStack} from 'platform/foundation';
import {object} from 'yup';

import {useEffect, useState} from 'react';

import {isNil} from 'ramda';
import {isNotNilOrEmpty, isNotString} from 'ramda-adjunct';

import {useGetInvoiceV4Query} from '@dms/api/finance';
import {FileResponseBody} from '@dms/api/shared';
import {useGetActiveSaleVehicleQuery} from '@dms/api/vehicle';
import {
  useIssueVehicleWarehouseMovementMutation,
  useLazyGetVehicleWarehouseMovementQuery,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';

import {getApiDateString, parseDate, suffixTestId, TestIdProps, yupString} from 'shared/web';

import {useDocumentTemplates} from '../../../hooks/useDocumentTemplates';
import {handleApiError} from '../../../utils/handleApiError';
import {
  DocumentTemplateBox,
  TemplateBoxTemplate,
} from '../../DocumentTemplateBox/DocumentTemplateBox';

interface VehicleWarehouseStockOutProps extends TestIdProps {
  vehicleId: string;
  invoiceId?: string;
  vehicleWarehouseId: string;
  onDocumentCreation: (document: FileResponseBody) => void;
  onMovementComplete?: () => void;
}

interface VehicleWarehouseStockOutFormFields {
  movementAt: string;
  note: string | null;
}

const STOCK_OUT_DOCUMENT_TEMPLATES_CODE = 'issue-vehicle-warehouse-movement';

export function VehicleWarehouseStockOut(props: VehicleWarehouseStockOutProps) {
  const [isDocumentSelected, setIsDocumentSelected] = useState(true);
  const [documentTemplate, setDocumentTemplate] = useState<TemplateBoxTemplate>();
  const [getTemplates, {isLoading: isTemplatesLoading, isError}] = useDocumentTemplates();

  const {defaultTemplates, selectedTemplates, selectedAuthProfileLabel, primaryTemplate} =
    getTemplates(STOCK_OUT_DOCUMENT_TEMPLATES_CODE);

  const {data: invoice, isLoading: isInvoiceLoading} = useGetInvoiceV4Query(
    {invoiceId: props.invoiceId ?? ''},
    {skip: isNil(props.invoiceId) || isNotString(props.invoiceId)}
  );

  const {data: activeSaleVehicle, isLoading: isActiveSaleVehicleLoading} =
    useGetActiveSaleVehicleQuery({
      vehicleId: props.vehicleId,
    });

  const [stockOutVehicle] = useIssueVehicleWarehouseMovementMutation();
  const [getMovement] = useLazyGetVehicleWarehouseMovementQuery();

  useEffect(() => {
    if (isNotNilOrEmpty(documentTemplate)) {
      return;
    }

    // eslint-disable-next-line react-hooks/set-state-in-effect
    setDocumentTemplate(primaryTemplate);
  }, [documentTemplate, primaryTemplate]);

  const onSubmit: FormSubmitHandler<VehicleWarehouseStockOutFormFields> = (values) =>
    stockOutVehicle({
      vehicleWarehouseId: props.vehicleWarehouseId,
      body: {
        vehicleId: props.vehicleId,
        movementAt: values.movementAt,
        issueTemplateId: isDocumentSelected ? documentTemplate?.id : undefined,
        note: values.note,
      },
    })
      .unwrap()
      .then((response) => {
        if (!isDocumentSelected) {
          showNotification.success(i18n.t('entity.vehicle.labels.vehicleStockedOut'));
          closeCurrentDialog();

          return;
        }

        return response;
      })
      .then((response) => {
        if (!response) {
          return;
        }

        return getMovement({
          vehicleWarehouseMovementId: response?.id,
        })
          .unwrap()
          .then((response) => {
            response.document && props.onDocumentCreation(response.document);
          });
      })
      .then(props.onMovementComplete)
      .catch(handleApiError);

  const defaultValues: Partial<VehicleWarehouseStockOutFormFields> = {
    movementAt:
      invoice?.dateOfTaxableSupply ??
      getApiDateString(parseDate(activeSaleVehicle?.soldAt) ?? new Date()),
  };

  const isLoading = isActiveSaleVehicleLoading || isInvoiceLoading || isTemplatesLoading;

  return (
    <VStack spacing={4}>
      <Text size="small" color="secondary">
        {i18n.t('entity.vehicleWarehouse.modal.stockOutDescription')}
      </Text>
      <DataStatus isLoading={isLoading} isError={isError}>
        <Form<VehicleWarehouseStockOutFormFields>
          defaultValues={defaultValues}
          onSubmit={onSubmit}
          schema={schema}
        >
          {(control, formApi) => (
            <VStack spacing={4}>
              <Grid columns={2}>
                <FormField
                  label={i18n.t('entity.vehicleWarehouse.labels.dateOfStockOut')}
                  name="movementAt"
                  type="apiDate"
                  control={control}
                  isRequired
                />
              </Grid>
              <DocumentTemplateBox
                data-testid={suffixTestId('documentTemplateBox', props)}
                title={i18n.t('entity.document.labels.vehicleWarehouseStockOutDocument')}
                templates={defaultTemplates}
                authorizationTemplates={selectedTemplates}
                authorizationProfileLabel={selectedAuthProfileLabel}
                selectedTemplate={documentTemplate}
                isSelected={isDocumentSelected}
                onSelect={setIsDocumentSelected}
                onTemplateChange={setDocumentTemplate}
                onNoteChange={(value) => formApi.setValue('note', value)}
                noteLabel={i18n.t('entity.document.labels.vehicleWarehouseStockOutNote')}
                isWithNote
                isDisabled
              />
              <ButtonGroup align="right">
                <Button
                  title={i18n.t('general.actions.cancel')}
                  variant="secondary"
                  onClick={closeCurrentDialog}
                />
                <FormButton
                  title={i18n.t('general.actions.confirm')}
                  type="submit"
                  control={control}
                />
              </ButtonGroup>
            </VStack>
          )}
        </Form>
      </DataStatus>
    </VStack>
  );
}

const schema = object({
  movementAt: yupString.required(),
  note: yupString,
});
